import { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ivrTrees } from '@/api/ivrTrees';
import type { IvrTreeRow } from '@/api/ivrTrees';
import { ivrNodes } from '@/api/ivrNodes';
import type { IvrNodeRow } from '@/api/ivrNodes';
import { ivrEdges } from '@/api/ivrEdges';
import type { IvrEdgeRow } from '@/api/ivrEdges';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useEffectiveCustomerId } from '@/lib/customer-context-store';
import { NodeEditorDrawer } from '@/components/ivr/NodeEditorDrawer';
import { NodeTypeBadge } from '@/components/ivr/NodeTypeBadge';
import { TreeListView } from '@/components/ivr/list/TreeListView';
import { IvrTreeCanvas } from './IvrTreeCanvas';
import { DataTable } from '@/components/DataTable';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { recordings } from '@/api/recordings';
import { cn } from '@/lib/cn';
import { formatRef } from '@/lib/formatRef';
import {
  Pencil, Check, X, Trash2, MoreHorizontal, Plus, Network,
  List, GitBranch, ArrowRight, RotateCcw, Recycle,
} from 'lucide-react';

// ── Helpers ───────────────────────────────────────────────────────────────────

const CAN_MUTATE = ['super_admin', 'reseller_owner', 'office_manager'];

type Tab = 'canvas' | 'nodes' | 'list' | 'edges' | 'recycle';

// ── Recording name cell ───────────────────────────────────────────────────────

function RecordingName({ id }: { id: string | null }) {
  const { data } = useQuery({
    queryKey: ['recording', id],
    queryFn: () => recordings.get(id!),
    enabled: !!id,
  });
  if (!id) return <span className="text-slate-300">—</span>;
  if (!data) return <span className="font-mono text-xs text-slate-400">…</span>;
  return <span className="text-xs text-slate-600 truncate max-w-[140px]">{data.name}</span>;
}

// ── Inline name editor ────────────────────────────────────────────────────────

function InlineNameEditor({ tree, canMutate }: { tree: IvrTreeRow; canMutate: boolean }) {
  const qc = useQueryClient();
  const customerId = useEffectiveCustomerId();
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(tree.name);
  const inputRef = useRef<HTMLInputElement>(null);

  const save = useMutation({
    mutationFn: () => ivrTrees.update(tree.id, { name: value.trim() }, { customerId }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['ivrTree', tree.id] });
      setEditing(false);
    },
  });

  function startEdit() {
    setValue(tree.name);
    setEditing(true);
    setTimeout(() => inputRef.current?.select(), 30);
  }

  function cancel() {
    setValue(tree.name);
    setEditing(false);
  }

  if (!editing) {
    return (
      <div className="flex items-center gap-2">
        <h1 className="text-2xl font-semibold tracking-tight text-slate-900">{tree.name}</h1>
        {canMutate && (
          <button
            type="button"
            onClick={startEdit}
            className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-colors"
            aria-label="Edit tree name"
          >
            <Pencil className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2">
      <input
        ref={inputRef}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && value.trim()) save.mutate();
          if (e.key === 'Escape') cancel();
        }}
        className="h-9 rounded-md border border-slate-300 px-3 text-xl font-semibold focus:outline-none focus:ring-2 focus:ring-slate-900"
        autoFocus
        aria-label="Tree name"
      />
      <button
        type="button"
        onClick={() => value.trim() && save.mutate()}
        disabled={save.isPending || !value.trim()}
        className="rounded p-1.5 text-green-600 hover:bg-green-50 disabled:opacity-40 transition-colors"
        aria-label="Save name"
      >
        <Check className="h-4 w-4" />
      </button>
      <button
        type="button"
        onClick={cancel}
        className="rounded p-1.5 text-slate-400 hover:bg-slate-100 transition-colors"
        aria-label="Cancel edit"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}

// ── Delete tree dialog ────────────────────────────────────────────────────────

function DeleteTreeDialog({ tree, onClose, onDeleted }: {
  tree: IvrTreeRow;
  onClose: () => void;
  onDeleted: () => void;
}) {
  const qc = useQueryClient();
  const customerId = useEffectiveCustomerId();
  const del = useMutation({
    mutationFn: () => ivrTrees.remove(tree.id, { customerId }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['ivrTrees'] });
      onDeleted();
    },
  });
  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Delete &ldquo;{tree.name}&rdquo;?</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-slate-600">
          All nodes and edges will be permanently deleted. This cannot be undone.
        </p>
        {del.isError && (
          <p role="alert" className="text-xs text-red-500">
            {del.error instanceof Error ? del.error.message : 'Delete failed.'}
          </p>
        )}
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline" disabled={del.isPending}>Cancel</Button>
          </DialogClose>
          <Button variant="destructive" disabled={del.isPending} onClick={() => del.mutate()}>
            {del.isPending ? 'Deleting…' : 'Delete tree'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Node active toggle ────────────────────────────────────────────────────────

function NodeActiveToggle({ node, canMutate }: { node: IvrNodeRow; canMutate: boolean }) {
  const qc = useQueryClient();
  const toggle = useMutation({
    mutationFn: () => ivrNodes.update(node.id, { active: !node.active }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['ivrNodes', node.tree_id] }),
  });
  return (
    <Switch
      checked={node.active}
      onCheckedChange={() => toggle.mutate()}
      disabled={!canMutate || toggle.isPending}
      aria-label={node.active ? 'Deactivate node' : 'Activate node'}
    />
  );
}

// ── Delete node confirm dialog ────────────────────────────────────────────────

function DeleteNodeDialog({ node, onClose }: { node: IvrNodeRow; onClose: () => void }) {
  const qc = useQueryClient();
  const [rootError, setRootError] = useState(false);
  const [childrenError, setChildrenError] = useState(false);
  const del = useMutation({
    mutationFn: () => ivrNodes.remove(node.id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['ivrNodes', node.tree_id] });
      qc.invalidateQueries({ queryKey: ['ivrNodesDeleted', node.tree_id] });
      qc.invalidateQueries({ queryKey: ['ivrEdgesForTree', node.tree_id] });
      onClose();
    },
    onError: (e) => {
      const msg = e instanceof Error ? e.message : '';
      setRootError(false);
      setChildrenError(false);
      if (msg.includes('node_is_tree_root') || msg.toLowerCase().includes('root')) {
        setRootError(true);
      } else if (msg.includes('has_children')) {
        setChildrenError(true);
      }
    },
  });

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Delete node &ldquo;{node.name}&rdquo;?</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-slate-600">
          The node will be moved to the Recycle Bin. Its incoming edge will be freed so the parent key slot can be reassigned.
        </p>
        {rootError && (
          <p role="alert" className="text-sm text-red-600">
            This node is the tree&apos;s root. Pick a different root node first, then retry.
          </p>
        )}
        {childrenError && (
          <p role="alert" className="text-sm text-red-600">
            This node has child nodes. Delete the children first before deleting this node.
          </p>
        )}
        {del.isError && !rootError && !childrenError && (
          <p role="alert" className="text-xs text-red-500">
            {del.error instanceof Error ? del.error.message : 'Delete failed.'}
          </p>
        )}
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline" disabled={del.isPending}>Cancel</Button>
          </DialogClose>
          <Button variant="destructive" disabled={del.isPending} onClick={() => del.mutate()}>
            {del.isPending ? 'Deleting…' : 'Move to recycle bin'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Tab bar ───────────────────────────────────────────────────────────────────

function TabBar({
  activeTab,
  onTabChange,
}: {
  activeTab: Tab;
  onTabChange: (t: Tab) => void;
}) {
  // Tree list comes first — that's the default landing tab and the
  // primary builder view (Sam 2026-05-21). Canvas + Nodes follow.
  const tabs: { id: Tab; label: string; icon: typeof Network }[] = [
    { id: 'list', label: 'Tree list', icon: List },
    { id: 'canvas', label: 'Canvas', icon: Network },
    { id: 'nodes', label: 'Nodes', icon: List },
    { id: 'edges', label: 'Edges', icon: GitBranch },
    { id: 'recycle', label: 'Recycle bin', icon: Recycle },
  ];

  return (
    <div className="flex items-center gap-0.5 border-b border-slate-100 px-6">
      {tabs.map(({ id, label, icon: Icon }) => (
        <button
          key={id}
          type="button"
          onClick={() => onTabChange(id)}
          className={cn(
            'flex items-center gap-1.5 px-3 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px',
            activeTab === id
              ? 'border-indigo-500 text-indigo-600'
              : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-200',
          )}
        >
          <Icon className="h-3.5 w-3.5" />
          {label}
        </button>
      ))}
    </div>
  );
}

// ── Edges tab ─────────────────────────────────────────────────────────────────

function EdgesTab({
  treeId,
  treeNodes,
  canMutate,
}: {
  treeId: string;
  treeNodes: IvrNodeRow[];
  canMutate: boolean;
}) {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['ivrEdgesForTree', treeId],
    queryFn: () => ivrEdges.listForTree(treeId),
  });

  const edges: IvrEdgeRow[] = data?.rows ?? [];

  const deleteEdge = useMutation({
    mutationFn: (id: string) => ivrEdges.remove(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['ivrEdgesForTree', treeId] });
    },
  });

  function nodeName(id: string | null) {
    if (!id) return <span className="text-slate-300 italic">—</span>;
    const n = treeNodes.find((x) => x.id === id);
    if (!n) return <span className="font-mono text-xs text-slate-400">{id.slice(0, 8)}…</span>;
    return (
      <span className="flex items-center gap-1.5">
        <span className="font-mono text-[10px] text-slate-400">{formatRef(n.ref_number)}</span>
        <span className="text-sm text-slate-700">{n.name}</span>
      </span>
    );
  }

  const edgeColumns = [
    {
      header: 'From',
      cell: (row: IvrEdgeRow) => nodeName(row.from_node_id),
    },
    {
      header: '',
      className: 'w-8 text-slate-300',
      cell: () => <ArrowRight className="h-3.5 w-3.5 text-slate-300" />,
    },
    {
      header: 'To',
      cell: (row: IvrEdgeRow) => nodeName(row.to_node_id),
    },
    {
      header: 'Branch key',
      cell: (row: IvrEdgeRow) => (
        <span className="font-mono text-xs rounded border border-slate-200 bg-slate-50 px-1.5 py-0.5 text-slate-700">
          {row.branch_key}
        </span>
      ),
    },
    {
      header: 'Label',
      cell: (row: IvrEdgeRow) =>
        row.label
          ? <span className="text-xs text-slate-500">{row.label}</span>
          : <span className="text-slate-300">—</span>,
    },
    ...(canMutate
      ? [{
          header: '',
          className: 'w-10 text-end',
          cell: (row: IvrEdgeRow) => (
            <button
              type="button"
              aria-label="Delete edge"
              className="rounded p-1 text-slate-400 hover:bg-red-50 hover:text-red-500 transition-colors"
              onClick={(e) => {
                e.stopPropagation();
                deleteEdge.mutate(row.id);
              }}
              disabled={deleteEdge.isPending}
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          ),
        }]
      : []),
  ];

  if (isLoading) {
    return <div className="text-sm text-slate-400 py-4">Loading edges…</div>;
  }

  return (
    <DataTable
      rows={edges}
      columns={edgeColumns}
      empty="No edges in this tree yet."
    />
  );
}

// ── Nodes tab ─────────────────────────────────────────────────────────────────

function NodesTab({
  treeId,
  tree,
  canMutate,
}: {
  treeId: string;
  tree: IvrTreeRow;
  canMutate: boolean;
}) {
  const qc = useQueryClient();

  const [nodeSearch, setNodeSearch] = useState('');
  const [debouncedNodeSearch, setDebouncedNodeSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState<string>('__all__');
  const [statusFilter, setStatusFilter] = useState<string>('__all__');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [drawerMode, setDrawerMode] = useState<'create' | 'edit' | null>(null);
  const [editNodeId, setEditNodeId] = useState<string | undefined>(undefined);
  const [deleteNodeRow, setDeleteNodeRow] = useState<IvrNodeRow | null>(null);
  const [bulkDeleteConfirm, setBulkDeleteConfirm] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  function handleNodeSearch(v: string) {
    setNodeSearch(v);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setDebouncedNodeSearch(v), 300);
  }

  const { data: nodesData, isLoading: nodesLoading } = useQuery({
    queryKey: ['ivrNodes', treeId, debouncedNodeSearch],
    queryFn: () => ivrNodes.listForTree(treeId, { search: debouncedNodeSearch || undefined }),
    enabled: !!treeId,
  });

  const allNodes: IvrNodeRow[] = nodesData?.rows ?? [];

  // Client-side type/status filters
  const nodes = allNodes.filter((n) => {
    if (typeFilter !== '__all__' && n.node_type !== typeFilter) return false;
    if (statusFilter === 'active' && !n.active) return false;
    if (statusFilter === 'inactive' && n.active) return false;
    return true;
  });

  // Bulk delete
  const bulkDelete = useMutation({
    mutationFn: async (ids: string[]) => {
      await Promise.all(ids.map((id) => ivrNodes.remove(id)));
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['ivrNodes', treeId] });
      qc.invalidateQueries({ queryKey: ['ivrEdgesForTree', treeId] });
      setSelectedIds(new Set());
      setBulkDeleteConfirm(false);
    },
  });

  function openCreateDrawer() {
    setEditNodeId(undefined);
    setDrawerMode('create');
  }

  function openEditDrawer(nodeId: string) {
    setEditNodeId(nodeId);
    setDrawerMode('edit');
  }

  function closeDrawer() {
    setDrawerMode(null);
    setEditNodeId(undefined);
  }

  function toggleSelect(id: string) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleSelectAll() {
    if (selectedIds.size === nodes.length && nodes.length > 0) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(nodes.map((n) => n.id)));
    }
  }

  // Unique node types from loaded data (for filter dropdown)
  const nodeTypes = Array.from(new Set(allNodes.map((n) => n.node_type))).sort();

  const nodeColumns = [
    ...(canMutate
      ? [{
          header: (
            <input
              type="checkbox"
              aria-label="Select all"
              checked={selectedIds.size === nodes.length && nodes.length > 0}
              onChange={toggleSelectAll}
              className="h-4 w-4 rounded border-slate-300 cursor-pointer"
            />
          ),
          className: 'w-10',
          cell: (row: IvrNodeRow) => (
            <input
              type="checkbox"
              aria-label={`Select ${row.name}`}
              checked={selectedIds.has(row.id)}
              onChange={() => toggleSelect(row.id)}
              onClick={(e) => e.stopPropagation()}
              className="h-4 w-4 rounded border-slate-300 cursor-pointer"
            />
          ),
        }]
      : []),
    {
      header: 'Ref',
      className: 'w-24',
      cell: (row: IvrNodeRow) => (
        <span className="font-mono text-xs text-slate-500">{formatRef(row.ref_number)}</span>
      ),
    },
    {
      header: 'Name',
      cell: (row: IvrNodeRow) => (
        <div className="flex items-center gap-1.5">
          <span className="font-medium text-slate-900">{row.name}</span>
          {row.id === tree.root_node_id && (
            <span className="inline-flex items-center rounded border border-amber-300 bg-amber-50 px-1 py-0 text-[9px] font-bold uppercase tracking-wide text-amber-700">
              root
            </span>
          )}
        </div>
      ),
    },
    {
      header: 'Type',
      cell: (row: IvrNodeRow) => <NodeTypeBadge type={row.node_type} showMvpPill />,
    },
    {
      header: 'Recording',
      cell: (row: IvrNodeRow) => <RecordingName id={row.main_recording_id} />,
    },
    {
      header: 'Active',
      cell: (row: IvrNodeRow) => (
        <NodeActiveToggle node={row} canMutate={canMutate} />
      ),
    },
    {
      header: '',
      className: 'w-10 text-end',
      cell: (row: IvrNodeRow) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              aria-label="Node actions"
              onClick={(e) => e.stopPropagation()}
              className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700 transition-colors"
            >
              <MoreHorizontal className="h-4 w-4" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
            <DropdownMenuItem onClick={() => openEditDrawer(row.id)}>
              <Pencil className="h-3.5 w-3.5 mr-2" />
              Edit
            </DropdownMenuItem>
            {canMutate && (
              <DropdownMenuItem
                className="text-red-600 focus:text-red-600 focus:bg-red-50"
                onClick={() => setDeleteNodeRow(row)}
              >
                <Trash2 className="h-3.5 w-3.5 mr-2" />
                Delete
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ];

  return (
    <div className="px-6 py-6 space-y-4">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2 flex-wrap">
          <Input
            value={nodeSearch}
            onChange={(e) => handleNodeSearch(e.target.value)}
            placeholder="Search nodes…"
            className="h-8 w-40 text-sm"
            aria-label="Search nodes"
          />

          {/* Type filter */}
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger className="h-8 w-40 text-xs">
              <SelectValue placeholder="All types" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All types</SelectItem>
              {nodeTypes.map((t) => (
                <SelectItem key={t} value={t}>
                  <span className="font-mono text-xs">{t}</span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Status filter */}
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="h-8 w-32 text-xs">
              <SelectValue placeholder="All statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All statuses</SelectItem>
              <SelectItem value="active">Active only</SelectItem>
              <SelectItem value="inactive">Inactive only</SelectItem>
            </SelectContent>
          </Select>

          {/* Bulk delete */}
          {canMutate && selectedIds.size > 0 && (
            <Button
              size="sm"
              variant="outline"
              className="h-8 text-xs border-red-200 text-red-600 hover:bg-red-50"
              onClick={() => setBulkDeleteConfirm(true)}
            >
              <Trash2 className="h-3.5 w-3.5 mr-1" />
              Delete {selectedIds.size} selected
            </Button>
          )}
        </div>

        <div className="flex items-center gap-2">
          <p className="text-xs text-slate-400">
            {nodes.length} node{nodes.length !== 1 ? 's' : ''}
          </p>
          {canMutate && (
            <Button size="sm" onClick={openCreateDrawer}>
              <Plus className="h-4 w-4 mr-1.5" />
              Add node
            </Button>
          )}
        </div>
      </div>

      {nodesLoading ? (
        <div className="text-sm text-slate-400">Loading nodes…</div>
      ) : (
        <DataTable
          rows={nodes}
          columns={nodeColumns}
          onRowClick={(row) => openEditDrawer(row.id)}
          empty={
            debouncedNodeSearch || typeFilter !== '__all__' || statusFilter !== '__all__'
              ? 'No nodes match the current filters.'
              : "No nodes yet. Click 'Add node' to create the first node in this tree."
          }
        />
      )}

      {/* Bulk delete confirm */}
      {bulkDeleteConfirm && (
        <>
          <div
            className="fixed inset-0 z-50 bg-black/20"
            onClick={() => setBulkDeleteConfirm(false)}
          />
          <div className="fixed left-1/2 top-1/2 z-50 w-[340px] -translate-x-1/2 -translate-y-1/2 rounded-xl border border-slate-200 bg-white p-5 shadow-xl">
            <p className="text-sm font-semibold text-slate-800">
              Delete {selectedIds.size} nodes?
            </p>
            <p className="mt-1 text-xs text-slate-500">
              This will permanently remove all selected nodes and their edges.
            </p>
            <div className="mt-4 flex gap-2">
              <button
                type="button"
                onClick={() => setBulkDeleteConfirm(false)}
                className="flex-1 rounded-md border border-slate-200 px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-50 transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => bulkDelete.mutate(Array.from(selectedIds))}
                disabled={bulkDelete.isPending}
                className="flex-1 rounded-md bg-red-500 px-3 py-1.5 text-sm font-medium text-white hover:bg-red-600 disabled:opacity-50 transition-colors"
              >
                {bulkDelete.isPending ? 'Deleting…' : 'Delete all'}
              </button>
            </div>
          </div>
        </>
      )}

      {/* Drawer */}
      {drawerMode && (
        <NodeEditorDrawer
          mode={drawerMode}
          treeId={treeId}
          nodeId={editNodeId}
          treeNodes={allNodes}
          canMutate={canMutate}
          onClose={closeDrawer}
          onSaved={() => {
            closeDrawer();
            qc.invalidateQueries({ queryKey: ['ivrNodes', treeId] });
          }}
          onDeleted={() => {
            closeDrawer();
            qc.invalidateQueries({ queryKey: ['ivrNodes', treeId] });
          }}
        />
      )}

      {deleteNodeRow && (
        <DeleteNodeDialog
          node={deleteNodeRow}
          onClose={() => setDeleteNodeRow(null)}
        />
      )}
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export function IvrTreeDetail() {
  const { id, tab } = useParams<{ id: string; tab?: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const user = useCurrentUser();
  const canMutate = CAN_MUTATE.includes(user.role);
  // Customer scope for IVT-N ref lookups. Set when the user has drilled
  // into a customer (via the scoped URL layout) — disambiguates
  // /api/ivr/trees/IVT-7 across customers. office_manager/below already
  // have an implicit scope via RLS, but threading it through still keeps
  // the lookup deterministic.
  const customerId = useEffectiveCustomerId();

  // Path-based tab routing (Sam 2026-05-21). The URL is now
  // /ivr/trees/:id/:tab where :tab ∈ list|canvas|nodes|edges|recycle.
  // Bare /ivr/trees/:id (no :tab segment) redirects to /list — the new
  // default. Legacy /ivr/trees/:id?tab=X URLs hit the bare-id route
  // here and get redirected to the corresponding /tab segment so
  // bookmarks keep working.
  const VALID_TABS: Tab[] = ['list', 'canvas', 'nodes', 'edges', 'recycle'];
  const tabFromPath: Tab | null = tab && (VALID_TABS as string[]).includes(tab)
    ? (tab as Tab)
    : null;
  const activeTab: Tab = tabFromPath ?? 'list';

  // Build the current customer-scoped URL prefix once so setTab and the
  // bare-id redirect both stay on the same scope.
  function urlForTab(t: Tab): string {
    // Strip a trailing /:tab if present so we always have the /:id prefix.
    const pathname = window.location.pathname;
    const idSegment = encodeURIComponent(id!);
    const idIdx = pathname.lastIndexOf(`/${idSegment}`);
    const base = idIdx >= 0 ? pathname.slice(0, idIdx + idSegment.length + 1) : pathname;
    // base now ends with "<id>/"; trim the trailing slash + any tab seg.
    const trimmed = base.replace(/\/+$/, '');
    return `${trimmed}/${t}${window.location.search}`;
  }

  function setTab(t: Tab) {
    navigate(urlForTab(t), { replace: false });
  }

  // Bare-id (or unknown tab) → redirect to /list once on mount. Also
  // handles legacy ?tab=X URLs: react-router strips the search but
  // useSearchParams isn't used here; we honor a ?tab= fallback below.
  useEffect(() => {
    if (!id) return;
    const legacyTab = new URLSearchParams(window.location.search).get('tab');
    if (!tabFromPath) {
      const target = legacyTab && (VALID_TABS as string[]).includes(legacyTab)
        ? (legacyTab as Tab)
        : 'list';
      navigate(urlForTab(target).replace(/\?tab=[^&]*/, ''), { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, tabFromPath]);

  const [deleteTreeOpen, setDeleteTreeOpen] = useState(false);

  const { data: tree, isLoading: treeLoading, error: treeError } = useQuery({
    // Key includes customerId so a super_admin switching customers
    // doesn't show a cached row from the previous customer's IVT-N.
    queryKey: ['ivrTree', id, customerId],
    queryFn: () => ivrTrees.get(id!, { customerId }),
    enabled: !!id,
  });

  // Nodes needed for the header's root-node selector (small list for the
  // select). NOTE: we deliberately pass `customerId` here so the backend
  // can resolve a ref-form `id` (e.g. `IVT-7`) to the right customer.
  // This query runs concurrently with the tree query — we can't switch
  // to `tree.id` because tree may not be loaded yet. Once tree resolves,
  // the queryKey changes (id stays the same, but the URL param's
  // semantics are unambiguous via customerId).
  const { data: nodesData } = useQuery({
    queryKey: ['ivrNodes', id, customerId],
    queryFn: () => ivrNodes.listForTree(id!, { customerId }),
    enabled: !!id,
  });
  const nodes: IvrNodeRow[] = nodesData?.rows ?? [];

  const setActive = useMutation({
    mutationFn: (active: boolean) => ivrTrees.update(id!, { active }, { customerId }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['ivrTree', id] }),
  });

  const setRoot = useMutation({
    mutationFn: (nodeId: string) => ivrTrees.setRoot(id!, nodeId, { customerId }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['ivrTree', id] }),
  });

  // ── Loading / error states ────────────────────────────────────────────────

  if (treeLoading) {
    return <div className="p-8 text-sm text-slate-400">Loading tree…</div>;
  }

  if (treeError || !tree) {
    return (
      <div className="p-8">
        <p role="alert" className="text-sm text-red-500">
          {treeError instanceof Error ? treeError.message : 'Tree not found.'}
        </p>
        <Button variant="outline" size="sm" className="mt-3" onClick={() => navigate('/ivr/trees')}>
          Back to IVR Trees
        </Button>
      </div>
    );
  }

  const rootNode = nodes.find((n) => n.id === tree.root_node_id);

  return (
    <div className="space-y-0">
      {/* ── Compact header — breadcrumb + name + controls all on one
              row when there's room, wrapping on narrow screens (Sam
              2026-05-21). Trims the page chrome so the tree body
              starts higher. */}
      <div className="border-b border-slate-100 px-6 pt-2 pb-2">
        {/* Row 1: breadcrumb (tiny). */}
        <button
          type="button"
          onClick={() => navigate('/ivr/trees')}
          className="flex items-center gap-1.5 text-[11px] text-slate-400 hover:text-slate-600 transition-colors mb-1"
        >
          <Network className="h-3 w-3" />
          IVR Trees
          <span className="text-slate-300">/</span>
          {tree.ref_number != null && (
            <span className="font-mono text-[10px] text-slate-400">
              {formatRef(tree.ref_number, 'IVT')}
            </span>
          )}
        </button>

        {/* Row 2: name + Active + Root node + Delete on one line. */}
        <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-2">
          <div className="flex flex-wrap items-center gap-x-5 gap-y-1.5">
            <InlineNameEditor tree={tree} canMutate={canMutate} />
            <div className="flex items-center gap-1.5">
              <span className="text-slate-500 text-xs">Active</span>
              <Switch
                checked={tree.active}
                onCheckedChange={(v) => setActive.mutate(v)}
                disabled={!canMutate || setActive.isPending}
                aria-label="Tree active"
              />
            </div>
            <div className="flex items-center gap-1.5">
              <span className="text-slate-500 text-xs">Root node</span>
              {canMutate ? (
                <Select
                  value={tree.root_node_id ?? '__none__'}
                  onValueChange={(v) => {
                    if (v !== '__none__') setRoot.mutate(v);
                  }}
                >
                  <SelectTrigger className="h-7 w-56 text-xs">
                    <SelectValue placeholder="— pick root —" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">— none —</SelectItem>
                    {nodes.map((n) => (
                      <SelectItem key={n.id} value={n.id}>
                        <span className="font-mono text-[11px] text-slate-400 mr-1">{formatRef(n.ref_number)}</span>
                        {n.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                rootNode
                  ? <span className="font-mono text-xs text-slate-600">{formatRef(rootNode.ref_number)} — {rootNode.name}</span>
                  : <span className="text-xs text-slate-300">— none —</span>
              )}
            </div>
          </div>
          {canMutate && (
            <Button
              variant="outline"
              size="sm"
              className="h-7 border-red-200 text-red-600 hover:bg-red-50 hover:border-red-300 shrink-0"
              onClick={() => setDeleteTreeOpen(true)}
            >
              <Trash2 className="h-3.5 w-3.5 mr-1.5" />
              Delete tree
            </Button>
          )}
        </div>
      </div>

      {/* ── Tabs ─────────────────────────────────────────────────────────────── */}
      <TabBar activeTab={activeTab} onTabChange={setTab} />

      {/* ── Tab content ──────────────────────────────────────────────────────── */}
      {activeTab === 'canvas' && (
        // React Flow needs an explicit pixel/calc height — h-full only
        // works when every ancestor up to the viewport has an explicit
        // height. The portal shell doesn't give us that, so dragging
        // the canvas was misbehaving (pan offsets calculated against a
        // collapsed/zero-height container). 100vh minus the header
        // chrome above gives a stable workspace (Sam 2026-05-21).
        <div className="h-[calc(100vh-180px)] min-h-[480px] w-full">
          <IvrTreeCanvas tree={tree} canMutate={canMutate} />
        </div>
      )}

      {/* Pass the RESOLVED UUID (tree.id) — NOT the URL param `id` —
          down to nested tabs. The URL param can be a ref form (`IVT-7`)
          which the nested endpoints (/api/ivr/trees/:treeId/nodes,
          /edges) treat as a UUID directly and 500/404 on. Using
          tree.id sidesteps the whole ref-resolution dance for every
          nested call. Bugfix 2026-05-17. */}
      {activeTab === 'nodes' && (
        <NodesTab treeId={tree.id} tree={tree} canMutate={canMutate} />
      )}

      {activeTab === 'list' && (
        <div className="px-6 pt-3 pb-6">
          <TreeListTab treeId={tree.id} canMutate={canMutate} />
        </div>
      )}

      {activeTab === 'edges' && (
        <div className="px-6 py-6">
          <div className="mb-4">
            <h2 className="text-base font-semibold text-slate-900">Edges</h2>
            <p className="text-xs text-slate-400 mt-0.5">All routing edges in this tree</p>
          </div>
          <EdgesTab treeId={tree.id} treeNodes={nodes} canMutate={canMutate} />
        </div>
      )}

      {activeTab === 'recycle' && (
        <div className="px-6 py-6">
          <NodeRecycleBinTab treeId={tree.id} canMutate={canMutate} />
        </div>
      )}

      {/* ── Dialogs ──────────────────────────────────────────────────────────── */}
      {deleteTreeOpen && (
        <DeleteTreeDialog
          tree={tree}
          onClose={() => setDeleteTreeOpen(false)}
          onDeleted={() => navigate('/ivr/trees')}
        />
      )}
    </div>
  );
}

// ── Node recycle bin tab ──────────────────────────────────────────────────────

function timeAgo(iso: string | null): string {
  if (!iso) return '—';
  const diff = Date.now() - new Date(iso).getTime();
  const secs = Math.floor(diff / 1000);
  if (secs < 60) return 'just now';
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
}

/**
 * Permanent-delete confirm dialog for recycle bin nodes.
 * Requires the user to type "delete" to confirm irreversible action.
 */
function PermanentDeleteNodeDialog({
  node,
  onClose,
}: {
  node: IvrNodeRow;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [confirmText, setConfirmText] = useState('');
  const del = useMutation({
    mutationFn: () => ivrNodes.removePermanent(node.id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['ivrNodesDeleted', node.tree_id] });
      onClose();
    },
  });
  const canConfirm = confirmText.trim().toLowerCase() === 'delete';
  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Permanently delete node?</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-sm text-slate-600">
          <p>
            <span className="font-medium text-slate-900">{node.name}</span>{' '}
            (<span className="font-mono text-xs">{formatRef(node.ref_number)}</span>) will be permanently
            removed and cannot be recovered.
          </p>
          <div className="rounded-md border border-red-100 bg-red-50 p-3 text-xs text-red-700">
            This cannot be undone. Type{' '}
            <span className="font-mono font-semibold">delete</span> below to confirm.
          </div>
          <input
            autoFocus
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && canConfirm && !del.isPending) del.mutate();
            }}
            placeholder="Type 'delete'"
            className="block w-full rounded-md border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-red-400 focus:outline-none focus:ring-1 focus:ring-red-400"
          />
        </div>
        {del.isError && (
          <p role="alert" className="text-xs text-red-500">
            {del.error instanceof Error ? del.error.message : 'Delete failed.'}
          </p>
        )}
        <DialogFooter>
          <DialogClose asChild>
            <Button type="button" variant="outline" disabled={del.isPending}>Cancel</Button>
          </DialogClose>
          <Button
            variant="destructive"
            disabled={!canConfirm || del.isPending}
            onClick={() => del.mutate()}
          >
            {del.isPending ? 'Deleting…' : 'Permanently delete'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function NodeRecycleBinTab({
  treeId,
  canMutate,
}: {
  treeId: string;
  canMutate: boolean;
}) {
  const qc = useQueryClient();
  const user = useCurrentUser();
  const canHardDelete = ['super_admin', 'reseller_owner'].includes(user.role);

  const [restoreNotice, setRestoreNotice] = useState<string | null>(null);
  const [permanentDeleteNode, setPermanentDeleteNode] = useState<IvrNodeRow | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['ivrNodesDeleted', treeId],
    queryFn: () => ivrNodes.listForTree(treeId, { deleted: true, limit: 200 }),
  });

  const rows: IvrNodeRow[] = data?.rows ?? [];

  const restore = useMutation({
    mutationFn: (id: string) => ivrNodes.restore(id),
    onSuccess: ({ relinked }) => {
      qc.invalidateQueries({ queryKey: ['ivrNodesDeleted', treeId] });
      qc.invalidateQueries({ queryKey: ['ivrNodes', treeId] });
      qc.invalidateQueries({ queryKey: ['ivrEdgesForTree', treeId] });
      if (relinked) {
        setRestoreNotice('Node restored and reconnected to its original position.');
      } else {
        setRestoreNotice(
          'Node restored. It is now unlinked — use the Edges tab or canvas to reconnect it.',
        );
      }
    },
    onError: (err) => {
      setRestoreNotice(
        `Restore failed: ${err instanceof Error ? err.message : 'unknown error'}`,
      );
    },
  });

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-base font-semibold text-slate-900">Node recycle bin</h2>
          <p className="text-xs text-slate-400 mt-0.5">
            Soft-deleted nodes for this tree. Restore to reconnect or permanently delete to
            free the slot.
          </p>
        </div>
      </div>

      {restoreNotice && (
        <div
          role="status"
          className="flex items-start justify-between gap-3 rounded-md border border-indigo-100 bg-indigo-50 px-3 py-2 text-sm text-indigo-700"
        >
          <span>{restoreNotice}</span>
          <button
            type="button"
            onClick={() => setRestoreNotice(null)}
            className="shrink-0 text-indigo-400 hover:text-indigo-600"
            aria-label="Dismiss"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {isLoading ? (
        <div className="text-sm text-slate-400">Loading…</div>
      ) : error ? (
        <div role="alert" className="text-sm text-red-500">
          {error instanceof Error ? error.message : 'Failed to load recycle bin.'}
        </div>
      ) : rows.length === 0 ? (
        <div className="rounded-md border border-dashed border-slate-200 p-10 text-center text-sm text-slate-500">
          The node recycle bin is empty.
        </div>
      ) : (
        <div className="rounded-md border border-slate-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-100 bg-slate-50 text-left text-xs font-medium text-slate-500">
                <th className="px-4 py-2.5">Ref</th>
                <th className="px-4 py-2.5">Name</th>
                <th className="px-4 py-2.5">Type</th>
                <th className="px-4 py-2.5">Deleted</th>
                <th className="px-4 py-2.5 text-end w-40">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((row) => (
                <tr key={row.id} className="hover:bg-slate-50">
                  <td className="px-4 py-2.5 font-mono text-xs text-slate-500">
                    {formatRef(row.ref_number)}
                  </td>
                  <td className="px-4 py-2.5 font-medium text-slate-700">{row.name}</td>
                  <td className="px-4 py-2.5">
                    <NodeTypeBadge type={row.node_type} showMvpPill={false} />
                  </td>
                  <td
                    className="px-4 py-2.5 text-xs text-slate-400"
                    title={row.deleted_at ? new Date(row.deleted_at).toLocaleString() : undefined}
                  >
                    {timeAgo(row.deleted_at)}
                  </td>
                  <td className="px-4 py-2.5">
                    <div className="flex items-center justify-end gap-1">
                      {canMutate && (
                        <button
                          type="button"
                          aria-label={`Restore ${row.name}`}
                          onClick={() => restore.mutate(row.id)}
                          disabled={restore.isPending && restore.variables === row.id}
                          className="inline-flex items-center gap-1 rounded border border-slate-200 bg-white px-2 py-1 text-xs font-medium text-slate-600 shadow-sm transition-colors hover:bg-slate-50 hover:text-slate-900 disabled:opacity-50"
                        >
                          <RotateCcw className="h-3.5 w-3.5" />
                          Restore
                        </button>
                      )}
                      {canHardDelete && (
                        <button
                          type="button"
                          aria-label={`Permanently delete ${row.name}`}
                          onClick={() => setPermanentDeleteNode(row)}
                          className="inline-flex items-center gap-1 rounded border border-transparent px-2 py-1 text-xs font-medium text-red-600 transition-colors hover:bg-red-50"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                          Delete
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {permanentDeleteNode && (
        <PermanentDeleteNodeDialog
          node={permanentDeleteNode}
          onClose={() => setPermanentDeleteNode(null)}
        />
      )}
    </div>
  );
}

// ── Tree list tab ─────────────────────────────────────────────────────────────
// The list tab is a *full alternative builder*: the IVR renders as an
// indented hierarchy and every node has an inline [+] to add a child
// without leaving the tab. Clicking a node opens the shared
// NodeEditorDrawer for in-place config.

function TreeListTab({ treeId, canMutate }: { treeId: string; canMutate: boolean }) {
  const qc = useQueryClient();
  const customerId = useEffectiveCustomerId();
  const [editNodeId, setEditNodeId] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<IvrNodeRow | null>(null);

  const { data: tree } = useQuery({
    // Same cache key shape as the outer IvrTreeDetail query so they
    // dedupe — both pull /api/ivr/trees/<id>?customer_id=<id> and
    // benefit from the same TanStack cache entry.
    queryKey: ['ivrTree', treeId, customerId],
    queryFn: () => ivrTrees.get(treeId, { customerId }),
  });
  const { data: nodesData } = useQuery({
    queryKey: ['ivrNodes', treeId],
    queryFn: () => ivrNodes.listForTree(treeId, { limit: 500 }),
  });
  const { data: edgesData } = useQuery({
    queryKey: ['ivrEdgesAll', treeId],
    queryFn: () => ivrEdges.listForTree(treeId),
  });

  const [removeError, setRemoveError] = useState<string | null>(null);
  const removeNode = useMutation({
    mutationFn: (id: string) => ivrNodes.remove(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['ivrNodes', treeId] });
      qc.invalidateQueries({ queryKey: ['ivrNodesDeleted', treeId] });
      qc.invalidateQueries({ queryKey: ['ivrEdgesAll', treeId] });
      qc.invalidateQueries({ queryKey: ['ivrEdgesForTree', treeId] });
      setDeleteTarget(null);
      setRemoveError(null);
    },
    onError: (e) => {
      const msg = e instanceof Error ? e.message : '';
      if (msg.includes('has_children')) {
        setRemoveError('This node has child nodes. Delete the children first before deleting this node.');
      } else if (msg.includes('node_is_tree_root')) {
        setRemoveError('This node is the tree\'s root. Pick a different root node first.');
      } else {
        setRemoveError(msg || 'Delete failed.');
      }
    },
  });

  if (!tree || !nodesData || !edgesData) {
    return <div className="text-sm text-slate-400">Loading…</div>;
  }

  return (
    <>
      <TreeListView
        treeId={treeId}
        nodes={nodesData.rows}
        edges={edgesData.rows}
        rootNodeId={tree.root_node_id}
        canMutate={canMutate}
        onEditNode={(n) => setEditNodeId(n.id)}
        onDeleteNode={canMutate ? (n) => setDeleteTarget(n) : undefined}
      />

      {editNodeId && (
        <NodeEditorDrawer
          mode="edit"
          treeId={treeId}
          nodeId={editNodeId}
          treeNodes={nodesData.rows}
          canMutate={canMutate}
          onClose={() => setEditNodeId(null)}
          onSaved={() => {
            setEditNodeId(null);
            qc.invalidateQueries({ queryKey: ['ivrNodes', treeId] });
            qc.invalidateQueries({ queryKey: ['ivrEdgesAll', treeId] });
          }}
          onDeleted={() => {
            setEditNodeId(null);
            qc.invalidateQueries({ queryKey: ['ivrNodes', treeId] });
            qc.invalidateQueries({ queryKey: ['ivrEdgesAll', treeId] });
          }}
        />
      )}

      {deleteTarget && (
        <Dialog open onOpenChange={(v) => { if (!v) { setDeleteTarget(null); setRemoveError(null); } }}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle>Delete node?</DialogTitle>
            </DialogHeader>
            <p className="text-sm text-slate-600">
              <span className="font-medium">{deleteTarget.name}</span>{' '}
              (<span className="font-mono text-xs">{formatRef(deleteTarget.ref_number)}</span>){' '}
              will be moved to the recycle bin. The incoming edge will be freed so the parent
              key slot can be reassigned.
            </p>
            {removeError && (
              <p role="alert" className="text-sm text-red-600">{removeError}</p>
            )}
            <DialogFooter>
              <DialogClose asChild>
                <Button type="button" variant="outline" disabled={removeNode.isPending}>Cancel</Button>
              </DialogClose>
              <Button
                variant="destructive"
                disabled={removeNode.isPending}
                onClick={() => removeNode.mutate(deleteTarget.id)}
              >
                {removeNode.isPending ? 'Deleting…' : 'Move to recycle bin'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}

